// @ts-check
import { RENDER_BUDGET_PER_BATCH, MIN_RENDER_BUDGET_BATCHES } from "../wgs/globals.js";

/** @import { Viewer3DImpl } from "./Viewer3DImpl" */

// We consider a frame an outlier, if it takes more than  OUTLIER_THRESHOLD_FACTOR times the
// the previous frame times (the actual threshold is dynamically adjusted every frame).
const OUTLIER_THRESHOLD_FACTOR = 2.3;

// We allow a few successive outliers, because it is quite common that a few frames are outliers,
// e.g. when garbage collection is performed.
const SUCCESSIVE_OUTLIER_LIMIT = 3;

// ---------------------------------------------------------------------------
// Render-budget controller (see updateRenderBudget)
//
// Goal: drive measured frame time toward the target (e.g. 33 ms) by adjusting the
// per-tick render budget (counted in batches). The controlled system is steep and
// non-linear (more budget usually means more frame time), and budget changes show up
// in measurements after 2 frames.
//
// Controller type: integral-only — budget corrections accumulate over time.
// Each frame (when the sample is budget-limited and enough viewers rendered):
//
//   1. Predict frame time — dead-time compensation: add the not-yet-observed effect of the
//      budget already commanded (slopeAvg × in-flight budget change) to the smoothed measurement,
//      then take error = target − predicted frame time. This keeps the loop from over-correcting
//      during the ~2-frame delay, which is what made large reductions slow to settle.
//
//   2. Estimate sensitivity — when the budget changed enough since the last sample,
//      compute how much frame time changed per batch of budget; smooth with an EMA
//      (slopeAvg). Ignore non-positive readings (noise / delay misalignment).
//
//   3. Compute update step — deltaBudget = gain × error / slopeAvg, so each step
//      has a similar frame-time impact regardless of scene steepness.
//
//   4. Rate-limit — clamp deltaBudget asymmetrically: reductions may be faster
//      (−MAX_STEP_FRACTION_DOWN) than increases (+MAX_STEP_FRACTION) of the current budget.
//
//   5. Accumulate — targetBudget += deltaBudget, then clamp to [MIN, MAX] (anti-windup;
//      bounds match RenderCommandSystem.updateTargetRenderBudget).
//
//   6. Apply — push the accumulated batch budget to each viewer's RCS.
//
// Special cases: ignore outlier frame times; on non-budget-limited frames (finished
// under target), skip the control loop but probe upward toward the budget that could
// theoretically fit within the target frame time (capped at 50% above consumed).
// ---------------------------------------------------------------------------

// Fraction of the frame-time error closed per correction. Kept well below 1 because several
// budget changes are in flight before their effect is measured (~2-frame feedback delay), so the
// effective correction per observed frame is roughly 3× this value.
const CTRL_LOOP_GAIN = 0.15;
// EMA smoothing for the measured sensitivity (frame-time change per batch of budget). Smooths the
// estimate used to scale corrections, not the raw inputs. It also drives the dead-time prediction,
// so it is kept fairly responsive: the estimate must re-converge quickly after the scene sensitivity
// changes (e.g. zooming in) or the predicted frame time — and thus the whole loop — reacts sluggishly.
const CTRL_SLOPE_SMOOTHING = 0.25;
// Maximum per-frame budget change as a fraction of the current budget. Safety net against a
// temporarily wrong sensitivity estimate; in steady state normalization keeps steps much smaller.
// Kept tight because the controlled system can be steep and non-linear (a small budget change can
// add a large amount of frame time on dense scenes).
// The rate limit is asymmetric: reductions may be faster than increases. Cutting the budget too far
// only renders a little less detail for a frame (progressive rendering catches up), whereas raising
// it too fast risks going over the frame budget. The faster reduction cap lets the controller react
// quickly when the budget must drop drastically (initial load, zooming in), while the tighter
// increase cap keeps it from overshooting. Both only bind on large errors, so the steady state
// (small steps) is unaffected.
const CTRL_MAX_STEP_FRACTION = 0.1; // increases
const CTRL_MAX_STEP_FRACTION_DOWN = 0.15; // reductions
// Clamp on the measured sensitivity (ms frame time per batch). The lower bound prevents a
// huge correction when frame time barely responds to budget (e.g. GPU-bound). The upper bound
// only guards against divide-by-near-zero; it must stay well above realistic sensitivities
// otherwise corrections are too large and the loop oscillates.

const CTRL_SLOPE_MIN = 0.01; // We can have 100 batches per MS in the point cloud case
const CTRL_SLOPE_MAX = 10.0; // 10ms per batch should be a quite conservative upper bound

// Minimum budget change (batches) required before a sample updates the sensitivity estimate.
// We want to have at least one batch of budget change to update the sensitivity estimate,
// but we allow for a bit of noise caused by having multiple viewers.
const CTRL_EXCITATION_MIN = 0.75 * RENDER_BUDGET_PER_BATCH;

// Light EMA on measured frame time (filters measurement noise; accumulated corrections do the rest).
const CTRL_FRAME_SMOOTHING = 0.5;
// On non-budget-limited frames, raise the accumulated target toward the budget that could
// theoretically fit within the target frame time, but by at most this fraction above the
// budget actually consumed (guards against overshoot during the ~2-frame feedback delay).
const CTRL_NON_BUDGET_LIMITED_UPWARD_MAX_FRACTION = 0.5;

/**
 * @typedef {Object} ViewerRenderBudgetStatus
 * @property {number} priority                      - The priority of the viewer.
 * @property {number} renderBudgetFraction          - The fraction of the render budget allocated to this viewer
 * @property {number|undefined} consumedRenderBudget - Batches consumed by this viewer on the last display frame
 * @property {boolean} isDone                       - Whether the viewer has finished rendering
 * @property {number} desiredFrameTime              - The desired frame time for this viewer (ms)
 * @property {number} maxRenderBudget                 - Maximum render budget for this viewer (RCS MAX_RENDER_BUDGET × fraction)
 *
 * @private
 */


export class RenderBudgetManager {
  /** @type {Map<Viewer3DImpl, ViewerRenderBudgetStatus>} */
  viewers = new Map();

  /** @type {number|undefined} Accumulated render-budget target at full budget allocation (all viewers active), in batches. Initialized on first usable sample. */
  targetBudget = undefined;
  /** Smoothed sensitivity: how much frame time (ms) changes per batch of render budget. */
  slopeAvg = 1.0;
  /** @type {number|undefined} Lightly smoothed measured frame time (ms). */
  /** @type {number|undefined} */frameTimeFiltered = undefined;
  /** Previous (budget, frame time) sample pair, used to estimate sensitivity. */
  /** @type {number|undefined} */prevBudgetMeasured = undefined;
  /** @type {number|undefined} */prevFrameTime = undefined;

  /** Diagnostics from the most recent controller update, for external inspection/tracking. */
  /** @type {number|undefined} */lastPredictedFrameTime = undefined;
  /** @type {number|undefined} */lastDeltaBudget = undefined;

  /** @type {number|undefined} Outlier tracking variables */
  outlierLimit = undefined;
  outlierCounter = 0;

  constructor() {
  }

  /**
   * Registers a new viewer with the manager.
   * @param {Viewer3DImpl} viewer - The viewer to register.
   * @param {number} priority - The priority of the viewer.
   */
  registerViewer(viewer, priority) {
    this.viewers.set(viewer, {
      priority: priority,
      renderBudgetFraction: 0,
      consumedRenderBudget: undefined,
      desiredFrameTime: 0,
      maxRenderBudget: 0,
      isDone: false
    });
    this._updateRenderBudgetFractions();
  }

  /**
   * Updates the priority of the given viewer.
   * @param {Viewer3DImpl} viewer - The viewer to register.
   * @param {number} priority - The priority of the viewer.
   */
  updateViewerPriority(viewer, priority) {
    let viewerStatus = this.viewers.get(viewer);
    if (!viewerStatus) return;

    viewerStatus.priority = priority;
    this._updateRenderBudgetFractions();
  }

  /**
   * Unregisters a viewer from the manager.
   * @param {Viewer3DImpl} viewer - The viewer to unregister.
   */
  unregisterViewer(viewer) {
    this.viewers.delete(viewer);
    this._updateRenderBudgetFractions();
    if (this.viewers.size === 0) {
      this._resetControllerState();
    }
  }

  /**
   * Clears accumulated controller state. Called when the last viewer is unregistered so a
   * subsequent registerViewer starts from a clean slate (e.g. model reload).
   * @private
   */
  _resetControllerState() {
    this.targetBudget = undefined;
    this.slopeAvg = 1.0;
    this.frameTimeFiltered = undefined;
    this.prevBudgetMeasured = undefined;
    this.prevFrameTime = undefined;
    this.outlierLimit = undefined;
    this.outlierCounter = 0;
    this.lastPredictedFrameTime = undefined;
    this.lastDeltaBudget = undefined;
  }

  /**
   * Update the render-budget fractions for all viewers.
   */
  _updateRenderBudgetFractions() {
    let totalPriority = 0;
    this.viewers.forEach((viewer) => {
      totalPriority += viewer.priority;
    });

    this.viewers.forEach((viewer) => {
      viewer.renderBudgetFraction = viewer.priority / totalPriority;
    });
  }

  /**
   * Estimate the render budget at full budget allocation (all viewers active, in batches) expected to produce the given frame time.
   * Inverts the controller's linear plant model from the most recent sample:
   * frameTime ≈ frameTimeFiltered + slopeAvg * (budget − prevBudgetMeasured).
   * Used by the deprecated ms-based CrossFadeEffects budget API to translate a desired frame time
   * into a batch budget.
   * @param {number} frameTimeMs - Desired frame time in milliseconds.
   * @returns {number|undefined} Estimated batch budget, or undefined if no usable measurement exists yet.
   */
  estimateRenderBudgetForFrameTime(frameTimeMs) {
    if (this.frameTimeFiltered === undefined || this.prevBudgetMeasured === undefined) {
      return undefined;
    }
    // slopeAvg is initialized to 1.0 and always clamped to [CTRL_SLOPE_MIN, CTRL_SLOPE_MAX], so it is safe to divide by.
    return this.prevBudgetMeasured + (frameTimeMs - this.frameTimeFiltered) / this.slopeAvg;
  }

  /**
   * Feed back measured frame time and consumed render budget to adjust the target budget.
   * @param {Viewer3DImpl} viewer
   * @param {boolean} isDone
   * @param {number|undefined} consumedRenderBudget - Batches consumed on the aligned display frame
   * @param {number|undefined} actualFrameTime - Wall-clock frame time (ms)
   * @param {number} desiredViewerFrameTime - Desired frame time for this viewer (ms)
   * @param {number} maxViewerRenderBudget - Maximum render budget for this viewer (batches)
   */
  updateRenderBudget(viewer, isDone, consumedRenderBudget, actualFrameTime, desiredViewerFrameTime, maxViewerRenderBudget) {
    let viewerStatus = this.viewers.get(viewer);
    if (!viewerStatus) return;

    if (actualFrameTime === undefined) {
      viewerStatus.consumedRenderBudget = undefined;
      return;
    }

    // If the outlier limit is not set, we initialize it to OUTLIER_THRESHOLD_FACTOR times the actual frame time.
    this.outlierLimit ??= OUTLIER_THRESHOLD_FACTOR * actualFrameTime;

    if (actualFrameTime > this.outlierLimit) {

      // It is quite common that a few successive frames are outliers, e.g. when garbage collection
      // is performed. Therefore, we just ignore up to SUCCESSIVE_OUTLIER_LIMIT outliers
      // (time the number of viewers, because each will report a frame time) in a row. Afterwards,
      // we start increasing the outlier limit exponentially.
      this.outlierCounter++;
      if (this.outlierCounter > SUCCESSIVE_OUTLIER_LIMIT * this.viewers.size) {
        this.outlierLimit = 2 * (this.outlierLimit + 1);
      }
      viewerStatus.consumedRenderBudget = undefined;
      return;
    } else {
      // If the frame time is not an outlier we decrease the outlier limit
      // smoothly to OUTLIER_THRESHOLD_FACTOR the frame time of the last frame.
      this.outlierLimit = 0.8 * this.outlierLimit + 0.2 * (OUTLIER_THRESHOLD_FACTOR * actualFrameTime);
      this.outlierCounter = Math.max(0, this.outlierCounter - 1);
    }

    // We have been called a second time for the same viewer. We assume this means that a
    // frame has been presented to the user and that we now have a valid frame time and can
    // use the consumed render budget to update the target budget.
    if (consumedRenderBudget !== undefined && viewerStatus.consumedRenderBudget !== undefined) {
      let allDone = true;
      let totalConsumedRenderBudget = 0;
      let desiredTotalFrameTime = 0;
      let totalMaxRenderBudget = 0;
      let usedRenderBudgetFraction = 0;
      for (let [, viewerStatus] of this.viewers) {
        if (viewerStatus.consumedRenderBudget !== undefined) {
          totalConsumedRenderBudget += viewerStatus.consumedRenderBudget;
          desiredTotalFrameTime += viewerStatus.desiredFrameTime;
          totalMaxRenderBudget += viewerStatus.maxRenderBudget;
          viewerStatus.consumedRenderBudget = undefined;
          usedRenderBudgetFraction += viewerStatus.renderBudgetFraction;
          allDone &&= viewerStatus.isDone;
        }
      }

      // We don't want to trigger updates if only a small fraction of the total render budget has
      // happened this frame (e.g. only the minimap has been redrawn). This factor is somewhat arbitrary, but it seems
      // reasonable that we should have done at least half of the total render budget to get a meaningful update.
      if (usedRenderBudgetFraction > 0.5) {

        // Scale to full budget allocation (as when all viewers are active and consume their allocated
        // share), since only a fraction of the viewers (usedRenderBudgetFraction of the total budget)
        // may have rendered this frame. With a uniform target frame time this makes the target exactly
        // TARGET_FRAME_TIME.
        const measuredFrameTime = actualFrameTime / usedRenderBudgetFraction;
        const measuredBudget = totalConsumedRenderBudget / usedRenderBudgetFraction;
        const target = desiredTotalFrameTime / usedRenderBudgetFraction;
        // Upper anti-windup bound, matching the RCS hard clamp (RenderCommandSystem.MAX_RENDER_BUDGET).
        const maxBudget = totalMaxRenderBudget / usedRenderBudgetFraction;

        // Budget-limited when work remains or frame time reached the target. Otherwise the frame
        // finished under target with all work done — the sample is uninformative for the control loop
        // (reducing budget from wall-clock time alone would underestimate demand), but frame-time
        // headroom can be used to probe how much budget could theoretically fit.
        const budgetLimited = !allDone || actualFrameTime >= desiredTotalFrameTime;
        let applyTargetBudget = false;

        // Initialize the accumulated budget on the first usable sample from the desired frame time.
        this.targetBudget ??= Math.max(RENDER_BUDGET_PER_BATCH, Math.round(target));

        if (budgetLimited) {
          // Step 1 — smooth measured frame time (input to dead-time prediction below).
          // Light smoothing on frame time (accumulated corrections and rate limit do the rest).
          this.frameTimeFiltered = this.frameTimeFiltered === undefined ?
          measuredFrameTime :
          (1 - CTRL_FRAME_SMOOTHING) * this.frameTimeFiltered + CTRL_FRAME_SMOOTHING * measuredFrameTime;

          // Step 2 — estimate sensitivity: update slopeAvg when the budget changed enough to measure an effect.
          // Clamped to [CTRL_SLOPE_MIN, CTRL_SLOPE_MAX]: the floor avoids a huge correction when
          // sensitivity is near zero (e.g. GPU-bound); the ceiling only guards against division by
          // near-zero and stays well above realistic values.
          if (this.prevFrameTime !== undefined &&
          this.prevBudgetMeasured !== undefined) {
            const deltaBudgetMeasured = measuredBudget - this.prevBudgetMeasured;
            if (Math.abs(deltaBudgetMeasured) >= CTRL_EXCITATION_MIN) {
              const slope = (this.frameTimeFiltered - this.prevFrameTime) / deltaBudgetMeasured;
              // More budget should increase frame time; a non-positive reading is noise or
              // feedback-delay misalignment. Ignoring it prevents dragging the estimate down
              // and over-correcting on the next frame.
              if (slope > 0) {
                const clampedSlope = Math.min(Math.max(slope, CTRL_SLOPE_MIN), CTRL_SLOPE_MAX);
                this.slopeAvg = (1 - CTRL_SLOPE_SMOOTHING) * this.slopeAvg + CTRL_SLOPE_SMOOTHING * clampedSlope;
              }
            }
          }

          // Step 1 — predict frame time: dead-time compensation (Smith-predictor style).
          // Budget corrections take ~2 frames to show up in the measurement, so the budget already
          // commanded but not yet observed (current targetBudget vs. the budget that actually produced
          // this sample) is still "in flight". Predict where frame time is heading once that settles
          // and steer on the prediction instead of the stale measurement. Without this, the loop keeps
          // correcting during the dead time, overshoots, and then slowly crawls back — which is what made
          // large budget reductions take many frames to converge. In steady state the commanded and
          // measured budgets coincide, so the term vanishes and adds no oscillation.
          const inFlightBudget = this.targetBudget - measuredBudget;
          const predictedFrameTime = this.frameTimeFiltered + this.slopeAvg * inFlightBudget;
          this.lastPredictedFrameTime = predictedFrameTime;

          // Adjust budget toward the target frame time, scaled by measured sensitivity so each step
          // has a predictable frame-time impact regardless of scene steepness.
          const error = target - predictedFrameTime;

          // Step 3 — compute update step.
          const deltaBudgetRaw = CTRL_LOOP_GAIN * error / this.slopeAvg;

          // Step 4 — rate-limit the per-frame budget change for stability under ~2-frame feedback delay.
          // Asymmetric — reductions may drop faster than increases rise (see CTRL_MAX_STEP_FRACTION).
          const maxStepUp = CTRL_MAX_STEP_FRACTION * this.targetBudget;
          const maxStepDown = CTRL_MAX_STEP_FRACTION_DOWN * this.targetBudget;
          const deltaBudget = Math.min(Math.max(deltaBudgetRaw, -maxStepDown), maxStepUp);
          this.lastDeltaBudget = deltaBudget;

          // Step 5 — accumulate and clamp (anti-windup; bounds match RenderCommandSystem.updateTargetRenderBudget).
          // Prevent the accumulated budget from growing without bound (also hard-clamped per viewer in RCS).
          this.targetBudget += deltaBudget;
          this.targetBudget = Math.min(Math.max(this.targetBudget, MIN_RENDER_BUDGET_BATCHES * RENDER_BUDGET_PER_BATCH), maxBudget);

          // Store sample pair for the next step-2 sensitivity update.
          this.prevBudgetMeasured = measuredBudget;
          this.prevFrameTime = this.frameTimeFiltered;

          applyTargetBudget = true;
        } else {
          // Finished under target: skip the control loop (sensitivity / frame-time filter stay
          // steady), but compare consumed vs. theoretical budget at target frame time and
          // pull the accumulated target upward toward that level (capped at 50% above consumed).

          // We use the linear slope here, because the measured slope might be very wrong,
          // if we have been in the non budget limited state for a while and didn't get any
          // meaningful samples.
          const linearSlope = measuredBudget / measuredFrameTime; // NaN if measuredFrameTime is 0; upward probe is skipped (NaN > n is false)
          const theoreticalBudgetAtTarget = measuredBudget + (target - measuredFrameTime) * linearSlope;
          if (theoreticalBudgetAtTarget > measuredBudget) {
            const upwardThreshold = Math.min(
              theoreticalBudgetAtTarget,
              measuredBudget * (1 + CTRL_NON_BUDGET_LIMITED_UPWARD_MAX_FRACTION)
            );
            if (upwardThreshold > this.targetBudget) {
              this.targetBudget = Math.min(upwardThreshold, maxBudget);
              applyTargetBudget = true;
            }
          }
        }

        // Step 6 — apply: push the accumulated batch budget to each viewer's RCS.
        if (applyTargetBudget) {
          for (let [viewerInstance, viewerStatus] of this.viewers) {
            viewerInstance._updateTargetRenderBudget(
              this.targetBudget * viewerStatus.renderBudgetFraction,
              viewerStatus.renderBudgetFraction
            );
          }
        }
      }
    }

    viewerStatus.consumedRenderBudget = consumedRenderBudget;
    viewerStatus.isDone = isDone;
    viewerStatus.desiredFrameTime = desiredViewerFrameTime;
    viewerStatus.maxRenderBudget = maxViewerRenderBudget;
  }
}


export let renderBudgetManager = new RenderBudgetManager();